// MUI 컴포넌트 기본값 / 스타일 오버라이드.
// 전역 테마에 병합되어 모든 화면에 적용된다(디자인 스펙 §1.6).

import type { Components, Theme } from "@mui/material/styles";
// @mui/x-data-grid의 테마 확장 타입을 로드해야 아래 MuiDataGrid 키가 유효해진다.
import type {} from "@mui/x-data-grid/themeAugmentation";

export const componentOverrides = (): Components<Theme> => ({
  // 헤더 AppBar: 파랑 풀블리드 → paper 배경 + 하단 1px divider (본문과 위계 정리)
  MuiAppBar: {
    defaultProps: { elevation: 0, color: "default" },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: "none",
      }),
    },
  },

  MuiPaper: {
    styleOverrides: {
      // 다크 모드 elevation 오버레이 그라데이션 제거
      root: { backgroundImage: "none" },
      outlined: ({ theme }) => ({ borderColor: theme.palette.divider }),
    },
  },

  MuiCard: {
    defaultProps: { variant: "outlined" },
    styleOverrides: { root: { borderRadius: 12, boxShadow: "none" } },
  },

  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: { borderRadius: 8, textTransform: "none", fontWeight: 600, paddingLeft: 14, paddingRight: 14 },
      containedPrimary: { boxShadow: "none", "&:hover": { boxShadow: "none" } },
    },
  },

  MuiTextField: { defaultProps: { size: "small", variant: "outlined" } },

  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
      }),
      input: { padding: "8.5px 12px", fontSize: "0.9375rem" },
    },
  },

  MuiInputLabel: { styleOverrides: { root: { fontSize: "0.9375rem" } } },
  MuiMenuItem: { styleOverrides: { root: { fontSize: "0.9375rem" } } },

  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 6, height: 24, fontSize: "0.8125rem", fontWeight: 600 },
      label: { paddingLeft: 8, paddingRight: 8 },
    },
  },

  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
        fontSize: "0.9375rem",
      }),
      columnHeaders: ({ theme }) => ({
        backgroundColor: theme.palette.grey[50],
        borderBottom: `2px solid ${theme.palette.divider}`,
      }),
      // 헤더 텍스트 2줄 wrap 허용 — 컬럼이 많아도 헤더가 잘리지 않게 하는 핵심
      columnHeaderTitle: {
        fontWeight: 700,
        fontSize: "0.8125rem",
        lineHeight: 1.3,
        whiteSpace: "normal",
      },
      cell: ({ theme }) => ({
        borderColor: theme.palette.divider,
        fontVariantNumeric: "tabular-nums",
        "&:focus, &:focus-within": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
      }),
      row: ({ theme }) => ({ "&:hover": { backgroundColor: theme.palette.action.hover } }),
      footerContainer: ({ theme }) => ({ borderTop: `1px solid ${theme.palette.divider}` }),
      columnSeparator: {
        visibility: "hidden",
        "&.MuiDataGrid-columnSeparator--resizable:hover": { visibility: "visible" },
      },
      toolbarContainer: { padding: "8px 12px", gap: 4 },
    },
  },

  MuiTablePagination: { styleOverrides: { toolbar: { fontSize: "0.9375rem" } } },

  // 접힌 사이드바에서 아이콘에 마우스를 올렸을 때 뜨는 페이지명 툴팁 — 기본값(0.6875rem)이
  // 너무 작아 읽기 힘들다는 피드백으로 확대.
  MuiTooltip: {
    styleOverrides: {
      tooltip: { fontSize: "0.875rem", padding: "6px 10px" },
    },
  },
});
